import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import useMediaQuery from '@mui/material/useMediaQuery';
import Divider from '@mui/material/Divider';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import HomeIcon from '@mui/icons-material/Home';
import MapIcon from '@mui/icons-material/Map';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import CompassWidget from './CompassWidget';
import { COLORS, TOOLTIP_STYLES } from '../styles/viewerStyles';

// Pre-computed isometric cube vertex positions
// Projection: azimuth=30°, elevation=20°, scale=72, center=(90,90) in 180×180 viewBox
const P = [
  { x: 40.8,  y: 119.3 }, // V0
  { x: 103.2, y: 107.0 }, // V1
  { x: 139.2, y: 128.3 }, // V2
  { x: 76.8,  y: 140.7 }, // V3
  { x: 40.8,  y: 51.7  }, // V4
  { x: 103.2, y: 39.3  }, // V5
  { x: 139.2, y: 60.7  }, // V6
  { x: 76.8,  y: 73.0  }, // V7
];

const CUBE_EDGES = [
  [0,1],[1,2],[2,3],[3,0],
  [4,5],[5,6],[6,7],[7,4],
  [0,4],[1,5],[2,6],[3,7],
];

// Each entry: the face verts to highlight, the HeadingPitchRange offset passed to
// viewer.flyTo(tileset, { offset }) — heading is the bearing FROM the dataset TO the
// camera (opposite of the direction the camera faces), and i18n key.
// Order: Top, North, East, South, West, Bottom.
const CUBE_VIEWS = [
  { verts: [4,5,6,7], heading: 0,             pitch: -Math.PI/2, labelKey: 'TopView' },
  { verts: [0,1,5,4], heading: Math.PI,       pitch: 0,          labelKey: 'NorthView' },
  { verts: [1,2,6,5], heading: 3*Math.PI/2,  pitch: 0,          labelKey: 'EastView' },
  { verts: [2,3,7,6], heading: 0,             pitch: 0,          labelKey: 'SouthView' },
  { verts: [3,0,4,7], heading: Math.PI/2,     pitch: 0,          labelKey: 'WestView' },
  { verts: [0,3,2,1], heading: 0,             pitch: Math.PI/2,  labelKey: 'BottomView' },
];

const CUBE_ACCENT = '#ffffff';
const CUBE_ACCENT_FILL = 'rgba(255, 255, 255, 0.34)';

function CubeFaceIcon({ faceIndex }) {
  const face = CUBE_VIEWS[faceIndex];
  const faceVertsSet = new Set(face.verts);
  const facePoints = face.verts.map(vi => `${P[vi].x},${P[vi].y}`).join(' ');

  return (
    <svg viewBox="0 0 180 180" width="26" height="26" style={{ overflow: 'visible', display: 'block' }}>
      <polygon points={facePoints} fill={CUBE_ACCENT_FILL} />
      {CUBE_EDGES.map(([a, b], i) => (
        <line
          key={i}
          x1={P[a].x} y1={P[a].y}
          x2={P[b].x} y2={P[b].y}
          stroke="rgba(255,255,255,1)"
          strokeWidth="5.5"
          strokeOpacity="0.35"
        />
      ))}
      {face.verts.map((vi, i) => {
        const next = face.verts[(i + 1) % face.verts.length];
        return (
          <line
            key={`h${i}`}
            x1={P[vi].x} y1={P[vi].y}
            x2={P[next].x} y2={P[next].y}
            stroke={CUBE_ACCENT}
            strokeWidth="7"
            strokeLinecap="round"
          />
        );
      })}
      {P.map((p, vi) => (
        <circle
          key={vi}
          cx={p.x} cy={p.y}
          r={faceVertsSet.has(vi) ? 3 : 1.5}
          fill={faceVertsSet.has(vi) ? CUBE_ACCENT : 'rgba(255,255,255,0.4)'}
        />
      ))}
    </svg>
  );
}

function NavigationCluster({
  viewer,
  onHeadingChange,
  onPitchChange,
  onZoomIn,
  onZoomOut,
  onRecenter,
  onFlyToAxisView,
  terrainEnabled,
  minimapOpen,
  onToggleMinimap,
  isWalkMode,
  onToggleWalkMode,
}) {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 600px)');
  const [opacity, setOpacity] = useState(1);
  const lastInteractionTime = useRef(Date.now());

  const handleInteraction = useCallback(() => {
    lastInteractionTime.current = Date.now();
    setOpacity(1);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      const elapsed = Date.now() - lastInteractionTime.current;
      if (elapsed > 3000) {
        setOpacity(0.3);
      }
    }, 500);
    return () => clearInterval(interval);
  }, []);

  const flyToView = useCallback((heading, pitch) => {
    onFlyToAxisView?.(heading, pitch);
  }, [onFlyToAxisView]);

  const buttonSx = {
    color: COLORS.white,
    borderRadius: '12px',
    width: 36,
    height: 36,
    transition: 'all 0.2s ease-in-out',
    bgcolor: 'transparent',
    '&:hover': {
      bgcolor: COLORS.hoverLight,
      transform: 'scale(1.05)',
    },
  };

  const divider = (
    <Divider
      flexItem
      sx={{ mx: 0.5, borderColor: 'rgba(255,255,255,0.12)' }}
    />
  );

  const barSx = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 0.5,
    bgcolor: COLORS.darkBg,
    backdropFilter: 'blur(16px)',
    border: `1px solid ${COLORS.panelBorder}`,
    boxShadow: COLORS.panelShadow,
    borderRadius: '16px',
    px: 0.5,
    py: 1,
  };

  return (
    <Box
      onMouseEnter={handleInteraction}
      onMouseMove={handleInteraction}
      onClick={handleInteraction}
      sx={{
        position: 'absolute',
        top: '50%',
        right: 8,
        transform: 'translateY(-50%)',
        zIndex: 1200,
        opacity,
        transition: 'opacity 0.4s ease',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '6px',
      }}
    >
      {/* Minimap toggle bar — above the view cube */}
      {onToggleMinimap && (
        <Box sx={barSx}>
          <Tooltip title={t('Minimap')} placement="left" arrow
            slotProps={{ tooltip: { sx: TOOLTIP_STYLES.tooltip }, arrow: { sx: TOOLTIP_STYLES.arrow } }}
          >
            <IconButton
              size="small"
              onClick={onToggleMinimap}
              sx={{
                ...buttonSx,
                bgcolor: minimapOpen ? COLORS.selectedLight : 'transparent',
                '&:hover': {
                  bgcolor: minimapOpen ? COLORS.highlightedBgHover : COLORS.hoverLight,
                  transform: 'scale(1.05)',
                },
              }}
            >
              <MapIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* View cube bar — axis-aligned camera positions */}
      <Box sx={barSx}>
        {CUBE_VIEWS.map((view, i) => {
          if (view.labelKey === 'BottomView' && terrainEnabled) return null;
          return (
            <Tooltip key={i} title={t(view.labelKey)} placement="left" arrow
              slotProps={{ tooltip: { sx: TOOLTIP_STYLES.tooltip }, arrow: { sx: TOOLTIP_STYLES.arrow } }}
            >
              <IconButton size="small" onClick={() => flyToView(view.heading, view.pitch)} sx={buttonSx}>
                <CubeFaceIcon faceIndex={i} />
              </IconButton>
            </Tooltip>
          );
        })}
      </Box> 

      <CompassWidget
        viewer={viewer}
        onHeadingChange={onHeadingChange}
        onPitchChange={onPitchChange}
      />

      {/* Zoom / recenter bar */}
      <Box sx={barSx}>
        <Tooltip title={t('ZoomIn')} placement="left" arrow
          slotProps={{ tooltip: { sx: TOOLTIP_STYLES.tooltip }, arrow: { sx: TOOLTIP_STYLES.arrow } }}
        >
          <IconButton size="small" onClick={onZoomIn} sx={buttonSx}>
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('RecenterCamera')} placement="left" arrow
          slotProps={{ tooltip: { sx: TOOLTIP_STYLES.tooltip }, arrow: { sx: TOOLTIP_STYLES.arrow } }}
        >
          <IconButton size="small" onClick={onRecenter} sx={buttonSx}>
            <HomeIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('ZoomOut')} placement="left" arrow
          slotProps={{ tooltip: { sx: TOOLTIP_STYLES.tooltip }, arrow: { sx: TOOLTIP_STYLES.arrow } }}
        >
          <IconButton size="small" onClick={onZoomOut} sx={buttonSx}>
            <RemoveIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Walk mode bar (desktop only) */}
      {!isMobile && onToggleWalkMode && (
        <Box sx={barSx}>
          <Tooltip title={t('WalkMode')} placement="left" arrow
            disableHoverListener={isWalkMode}
            disableFocusListener={isWalkMode}
            disableTouchListener={isWalkMode}
            slotProps={{ tooltip: { sx: TOOLTIP_STYLES.tooltip }, arrow: { sx: TOOLTIP_STYLES.arrow } }}
          >
            <IconButton
              size="small"
              onClick={onToggleWalkMode}
              sx={{
                ...buttonSx,
                bgcolor: isWalkMode ? COLORS.selectedLight : 'transparent',
                '&:hover': {
                  bgcolor: isWalkMode ? COLORS.highlightedBgHover : COLORS.hoverLight,
                  transform: 'scale(1.05)',
                },
              }}
            >
              <DirectionsWalkIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
}

NavigationCluster.propTypes = {
  viewer: PropTypes.object,
  onHeadingChange: PropTypes.func,
  onPitchChange: PropTypes.func,
  onZoomIn: PropTypes.func,
  onZoomOut: PropTypes.func,
  onRecenter: PropTypes.func,
  onFlyToAxisView: PropTypes.func,
  terrainEnabled: PropTypes.bool,
  minimapOpen: PropTypes.bool,
  onToggleMinimap: PropTypes.func,
  isWalkMode: PropTypes.bool,
  onToggleWalkMode: PropTypes.func,
};

export default NavigationCluster;
